import React, {useEffect, useRef, useState} from 'react';
import {Alert, Button, Chip, CircularProgress, Divider, Link, Stack, Typography} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {Link as RouterLink, useLocation, useNavigate} from 'react-router';
import {useSession} from '../../../firebase/UserProvider';
import {getUsersByIds} from '../../../firebase/firestore/users';
import AuthorChip from '../../products/components/common/AuthorChip';
import EntityTagsSection, {TAGS_SECTION_HEADING_SX} from '../../collection-requests/tags/EntityTagsSection';
import SourcesFileViewerModal from '../../products/reportEditorPage/leftPanel/components/SourcesFileViewerModal';
import IirBodyRenderer from '../components/IirBodyRenderer';
import LinkedEeiCardsSection from '../components/LinkedEeiCardsSection';
import {collectionTaskPath, hasAnyRole, IIR_TAG_ROLES} from '../collectionTaskPayload';
import {getCollectionIir, getCollectionTask} from '../collectionTaskApi';
import {
    canShowIirViewEditButton,
    navigateToIirEditor,
    shouldOpenIirEditor,
} from './openCollectionIir';
import {readCollectionMaterialViewerState} from './openCollectionMaterial';

function formatDate(value) {
    return value ? new Date(value).toLocaleString() : '—';
}

function userDisplayName(u) {
    if (!u) return null;
    const fromParts = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return fromParts || u.displayName || u.name || u.email || null;
}

function statusChipProps(workflowStatus) {
    if (workflowStatus === 'approved') {
        return {label: 'Approved', color: 'success', variant: 'filled'};
    }
    if (workflowStatus === 'submitted') {
        return {label: 'Submitted', color: 'info', variant: 'filled'};
    }
    return {label: 'Draft', color: 'default', variant: 'outlined'};
}

/**
 * Gray metadata header for IIRs in the viewer modal. Includes role-based REPORT
 * tags, an Edit affordance for admins only, parent task (ID + title) link, and
 * linked EEI cards between metadata and tags.
 */
const IIR_HEADER_FIELDS = [
    {
        key: 'status',
        label: 'Status',
        render: (iir) => {
            const chip = statusChipProps(iir.workflowStatus || iir.status);
            return <Chip size="small" label={chip.label} color={chip.color} variant={chip.variant}/>;
        },
    },
    {
        key: 'parentTask',
        label: 'Parent Task',
        render: (iir, {taskTitle}) => {
            const key = iir.ctNumber || iir.collectionTaskId;
            if (!key) {
                return <Typography variant="body2">—</Typography>;
            }
            const label = [iir.ctNumber || iir.collectionTaskId, taskTitle || iir.taskTitle]
                .filter(Boolean)
                .join(' · ');
            return (
                <Link
                    component={RouterLink}
                    to={collectionTaskPath(key)}
                    underline="hover"
                    style={{fontFamily: 'monospace'}}
                >
                    {label}
                </Link>
            );
        },
    },
    {
        key: 'author',
        label: 'Author',
        render: (iir, {authorProfile}) => (iir.ownerUserId ? (
            <AuthorChip
                userId={iir.ownerUserId}
                userDisplayName={userDisplayName(authorProfile) || iir.ownerUserId}
                flex={undefined}
            />
        ) : (
            <Typography variant="body2">—</Typography>
        )),
    },
    {
        key: 'createdAt',
        label: 'Created',
        render: (iir) => <Typography variant="body2">{formatDate(iir.createdAt)}</Typography>,
    },
    {
        key: 'updatedAt',
        label: 'Updated',
        render: (iir) => <Typography variant="body2">{formatDate(iir.updatedAt)}</Typography>,
    },
];

export function IirViewerHeader({
    iir,
    eeis,
    taskTitle = null,
    canEditTags = false,
    onOpenEditor = null,
}) {
    const [authorProfile, setAuthorProfile] = useState(null);

    const ownerUserId = iir?.ownerUserId || null;
    useEffect(() => {
        let cancelled = false;
        setAuthorProfile(null);
        if (!ownerUserId) return undefined;
        getUsersByIds([ownerUserId])
            .then((users) => {
                if (!cancelled) setAuthorProfile(users?.[0] || null);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [ownerUserId]);

    if (!iir) return null;

    const renderCtx = {authorProfile, taskTitle};

    return (
        <Stack spacing={1.5} p={2} data-testid="iir-viewer-header">
            <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
                <Typography variant="h6" sx={{lineHeight: 1.3, minWidth: 0, width: '100%'}}>
                    {iir.title || 'Untitled IIR'}
                </Typography>
                {onOpenEditor && (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditOutlinedIcon fontSize="small"/>}
                        onClick={onOpenEditor}
                        sx={{whiteSpace: 'nowrap', flexShrink: 0}}
                    >
                        Edit
                    </Button>
                )}
            </Stack>

            <Stack spacing={1.5} p={2} bgcolor="#F9F9F9" borderRadius={1}>
                <Stack
                    direction="row"
                    flexWrap="wrap"
                    useFlexGap
                    columnGap={4}
                    rowGap={1.25}
                >
                    {IIR_HEADER_FIELDS.map(({key, label, render}) => (
                        <Stack key={key} spacing={0.25} minWidth={0}>
                            <Typography sx={TAGS_SECTION_HEADING_SX}>{label}</Typography>
                            {render(iir, renderCtx)}
                        </Stack>
                    ))}
                </Stack>

                <LinkedEeiCardsSection
                    linkedEeis={eeis}
                    showHeading
                    collapsible
                    defaultExpanded={false}
                />

                <Divider/>

                <EntityTagsSection
                    entityType="REPORT"
                    entityId={iir.reportId || iir.id}
                    canEdit={canEditTags}
                    enabled={Boolean(iir.reportId || iir.id)}
                    dense
                />
            </Stack>
        </Stack>
    );
}

/**
 * IIR flavor of the C2 viewer: composes SourcesFileViewerModal with IirBodyRenderer.
 * Author + Draft opens are redirected to the editor; Edit on view is admin-only.
 */
export default function IirMaterialViewer({reportId, onClose, onBack}) {
    const navigate = useNavigate();
    const location = useLocation();
    const {user, role, roles} = useSession() || {};

    const [iir, setIir] = useState(null);
    const [eeis, setEeis] = useState([]);
    const [taskTitle, setTaskTitle] = useState(null);
    const [error, setError] = useState(null);
    const redirectedRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        setIir(null);
        setEeis([]);
        setTaskTitle(null);
        setError(null);
        redirectedRef.current = false;
        if (!reportId) return undefined;
        getCollectionIir(reportId)
            .then(async (summary) => {
                if (cancelled) return;
                if (!summary) {
                    setError('IIR not found');
                    return;
                }
                // Author + Draft → editor (preserve viewer returnTo for Back).
                if (!redirectedRef.current && shouldOpenIirEditor({iir: summary, userId: user?.uid})) {
                    redirectedRef.current = true;
                    const viewerState = readCollectionMaterialViewerState(location.search);
                    navigateToIirEditor({
                        reportId,
                        navigate,
                        returnTo: viewerState?.returnTo || undefined,
                        location,
                    });
                    return;
                }
                setIir(summary);
                if (summary.collectionTaskId) {
                    try {
                        const task = await getCollectionTask(summary.collectionTaskId);
                        if (!cancelled) {
                            setEeis(Array.isArray(task?.eeis) ? task.eeis : []);
                            setTaskTitle(summary.taskTitle || task?.title || null);
                        }
                    } catch {
                        if (!cancelled) {
                            setEeis([]);
                            setTaskTitle(summary.taskTitle || null);
                        }
                    }
                } else {
                    setTaskTitle(summary.taskTitle || null);
                }
            })
            .catch((e) => {
                if (!cancelled) setError(e?.message || 'Failed to load IIR');
            });
        return () => {
            cancelled = true;
        };
    }, [reportId, user?.uid, navigate, location]);

    let contentSlot;
    if (error) {
        contentSlot = <Alert severity="error">{error}</Alert>;
    } else if (!iir) {
        contentSlot = (
            <Stack flex={1} alignItems="center" justifyContent="center">
                <CircularProgress size={28}/>
            </Stack>
        );
    } else {
        // Parent modal owns unified scroll (header + body); don't nest a second scrollbar.
        contentSlot = (
            <IirBodyRenderer
                reportId={reportId}
                sx={{flex: 'none', minHeight: 'auto', overflowY: 'visible', px: 0, py: 0}}
            />
        );
    }

    const canEditTags = hasAnyRole(role, roles, IIR_TAG_ROLES);
    const canOpenEditor = canShowIirViewEditButton({role, roles});

    return (
        <SourcesFileViewerModal
            controlled={{
                isOpen: true,
                fileId: null,
                canonicalFileId: null,
                fileName: iir?.title || 'IIR',
                fileType: 'iir',
                fileSource: 'iir',
                onClose,
            }}
            headerExpand
            headerSlot={iir
                ? (
                    <IirViewerHeader
                        iir={iir}
                        eeis={eeis}
                        taskTitle={taskTitle}
                        canEditTags={canEditTags}
                        onOpenEditor={canOpenEditor
                            ? () => {
                                const viewerState = readCollectionMaterialViewerState(location.search);
                                navigateToIirEditor({
                                    reportId,
                                    navigate,
                                    returnTo: viewerState?.returnTo || undefined,
                                    location,
                                });
                            }
                            : null}
                    />
                )
                : null}
            contentSlot={contentSlot}
            onBack={onBack}
        />
    );
}
